import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X, Settings } from "lucide-react";

if (typeof window !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement("#root");
}

const EditMerchantInventoryModal = ({ item, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    stockAmount: 0,
    criticalLevel: 0,
    replenishAmount: 0,
    isAutoReplenishEnabled: true,
    unitPrice: "",
    category: "",
    uom: "",
    suggestedMaxQty: 0,
    uomSmallStr: "",
    srpSmallStr: "",
    uomRegularPdl: "",
    srpRegularPdl: ""
  });

  useEffect(() => {
    if (!isOpen || !item) return;
    setForm({
      stockAmount: item.qty ?? 0,
      criticalLevel: item.criticalLevel ?? 0,
      replenishAmount: item.replenishAmount ?? 0,
      isAutoReplenishEnabled: item.autoReplenishment ?? true,
      unitPrice: item.cost ?? "",
      category: item.category ?? "Market",
      uom: item.uom ?? "",
      suggestedMaxQty: item.maxLevel ?? item.replenishAmount ?? 0,
      uomSmallStr: item.uomSmallStr ?? "Pack, Piece",
      srpSmallStr: item.srpSmallStr ?? "",
      uomRegularPdl: item.uomRegularPdl ?? item.uom ?? "Piece",
      srpRegularPdl: item.srpRegularPdl ?? item.srp ?? ""
    });
  }, [isOpen, item]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!item || !onSave) return;
    const status = String(item?.status ?? "").toUpperCase();
    if (status === "INACTIVE") return;
    try {
      await onSave(item, {
        stockAmount: Number(form.stockAmount) || 0,
        criticalLevel: Number(form.criticalLevel) || 0,
        replenishAmount: Number(form.replenishAmount) || 0,
        isAutoReplenishEnabled: Boolean(form.isAutoReplenishEnabled)
      });
      onClose();
    } catch (err) {
      console.error("Failed to save inventory:", err);
    }
  };

  if (!item && !isOpen) return null;
  const isItemInactive = String(item?.status ?? "").toUpperCase() === "INACTIVE";

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { backgroundColor: "rgba(0, 0, 0, 0.75)", zIndex: 1000 },
        content: {
          position: "relative",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          maxWidth: "640px",
          width: "100%",
          maxHeight: "90vh",
          margin: "auto",
          padding: "0",
          outline: "none",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.2)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }
      }}
      contentLabel="Edit Product"
    >
      <div className="edit-merchant-inventory-content">
        <button className="edit-merchant-inventory-close" onClick={onClose} aria-label="Close">
          <X size={24} />
        </button>

        <div className="edit-merchant-inventory-top">
          <div className="edit-merchant-inventory-image">
            <img
              src={item?.imageUrl || "https://via.placeholder.com/80x80?text=mockup"}
              alt={item?.item || "Product"}
            />
          </div>
          <div className="edit-merchant-inventory-header-right">
            <div className="edit-merchant-inventory-title-block">
              <h2 className="edit-merchant-inventory-title">{item?.item || "Product"}</h2>
              <span className="edit-merchant-inventory-sku">{item?.sku || "SKU-001"}</span>
            </div>
            <div className="edit-merchant-inventory-basic-grid">
              <div className="edit-merchant-inventory-basic-col">
                <div className="edit-merchant-inventory-field edit-merchant-inventory-basic">
                  <label>Unit Price</label>
                  <input
                    type="text"
                    value={form.unitPrice}
                    readOnly
                    tabIndex={-1}
                    placeholder="₱120"
                  />
                </div>
                <div className="edit-merchant-inventory-field edit-merchant-inventory-basic">
                  <label>Stock Amount</label>
                  <input
                    type="number"
                    value={form.stockAmount}
                    onChange={(e) => handleChange("stockAmount", e.target.value)}
                  />
                </div>
              </div>
              <div className="edit-merchant-inventory-basic-col">
                <div className="edit-merchant-inventory-field edit-merchant-inventory-basic">
                  <label>UOM</label>
                  <input
                    type="text"
                    value={form.uom}
                    readOnly
                    tabIndex={-1}
                    placeholder="Box"
                  />
                </div>
                <div className="edit-merchant-inventory-field edit-merchant-inventory-basic">
                  <label>Category</label>
                  <input
                    type="text"
                    value={form.category}
                    readOnly
                    tabIndex={-1}
                    placeholder="Market"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="edit-merchant-inventory-auto-replenish">
          <Settings size={18} className="edit-merchant-inventory-gear" />
          <span>Auto-Replenish</span>
          <label className="merchant-detail-toggle">
            <input
              type="checkbox"
              checked={form.isAutoReplenishEnabled}
              onChange={(e) => handleChange("isAutoReplenishEnabled", e.target.checked)}
              className="merchant-detail-toggle-input"
            />
            <span className="merchant-detail-toggle-slider"></span>
          </label>
        </div>

        <div className="edit-merchant-inventory-replenish-grid">
          <div className="edit-merchant-inventory-field edit-merchant-inventory-field-accent">
            <label>Re-order Level QTY</label>
            <input
              type="number"
              value={form.criticalLevel}
              onChange={(e) => handleChange("criticalLevel", e.target.value)}
            />
            <span className="edit-merchant-inventory-hint">Replenish when stock falls below this number</span>
          </div>
          <div className="edit-merchant-inventory-field">
            <label>Suggested Replenish QTY</label>
            <input
              type="number"
              value={form.replenishAmount}
              onChange={(e) => handleChange("replenishAmount", e.target.value)}
            />
            <span className="edit-merchant-inventory-hint">Quantity to replenish when stock falls below re-order level</span>
          </div>
          <div className="edit-merchant-inventory-field">
            <label>Suggested Maximum QTY</label>
            <input
              type="number"
              value={form.suggestedMaxQty}
              readOnly
              tabIndex={-1}
            />
            <span className="edit-merchant-inventory-hint">Suggested maximum stock level for this item</span>
          </div>
        </div>

        <div className="edit-merchant-inventory-grid edit-merchant-inventory-uom-srp">
          <div className="edit-merchant-inventory-field">
            <label>UOM for Small STR</label>
            <input
              type="text"
              value={form.uomSmallStr}
              readOnly
              tabIndex={-1}
              placeholder="Pack, Piece"
            />
          </div>
          <div className="edit-merchant-inventory-field">
            <label>SRP for Small STR</label>
            <input
              type="text"
              value={form.srpSmallStr}
              readOnly
              tabIndex={-1}
              placeholder=""
            />
          </div>
          <div className="edit-merchant-inventory-field">
            <label>UOM for Regular PDL</label>
            <input
              type="text"
              value={form.uomRegularPdl}
              readOnly
              tabIndex={-1}
              placeholder="Piece"
            />
          </div>
          <div className="edit-merchant-inventory-field">
            <label>SRP for Regular PDL</label>
            <input
              type="text"
              value={form.srpRegularPdl}
              readOnly
              tabIndex={-1}
              placeholder=""
            />
          </div>
        </div>

        <div className="edit-merchant-inventory-actions">
          <button type="button" className="edit-merchant-inventory-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="edit-merchant-inventory-save"
            onClick={handleSave}
            disabled={isItemInactive}
            aria-disabled={isItemInactive}
            title={isItemInactive ? "Cannot save while item is inactive" : undefined}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default EditMerchantInventoryModal;
